import Button, { BUTTON_VARIANTS } from '@/atoms/Button';
import SearchBar from '@/atoms/forms/SearchBar';
import TotalResults from '@/atoms/TotalResults';
import DropDown from '@/molecules/DropDown';
import BigTabs from '@/molecules/Tabs/BigTabs';
import TabsLink from '@/molecules/Tabs/TabsLink';
import clx from 'classnames';
import { CARDS_MAX_RESULTS } from 'constants/maxResults';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { ParsedQuery } from 'query-string';
import { FormProvider } from 'react-hook-form';
import { FilterDataDto, SIZE } from 'types/utils';
import useGetCmsToggle from 'utils/cms/useGetCmsToggle';
import ActiveFilters from './components/ActiveFilters';
import useListHeader from './useListHeader';
import { filterJsonType } from '@/types/filterJson';

const ListHeader = ({
  loading,
  filterData,
  totalItems,
  onFilterSubmit,
  // TODO To remove, it must be guessed from the setFilters function if it exist
  updateQueryParams,
  filtersValues,
  onClear,
  withOrder = true,
  setOrder,
  shallowRouting = false,
  filterJson,
}: {
  loading?: boolean;
  filterData?: FilterDataDto;
  totalItems?: number;
  onFilterSubmit?: (filters: ParsedQuery) => void;
  updateQueryParams?: boolean;
  filtersValues?: ParsedQuery;
  onClear?: () => void;
  withOrder?: boolean;
  setOrder?: (name: string, value: string) => void;
  // Used to navigate without going by getServerSideProps
  shallowRouting?: boolean;
  filterJson?: filterJsonType[];
}) => {
  const { t } = useTranslation();
  const {
    openModal,
    formMethods,
    handleSubmit,
    searchBar,
    optionsOrder,
    activeFilterCount,
    onClear: listHeaderOnClear,
    onFieldReset,
    showModal,
    tabs,
  } = useListHeader({ filterData, onFilterSubmit, updateQueryParams, filtersValues, filterJson });
  const onClearFilters = () => {
    listHeaderOnClear();
    if (onClear) {
      onClear();
    }
  };
  const items = useGetCmsToggle({ tabsName: 'toggle' });

  return (
    <FormProvider {...formMethods}>
      <form className="flex flex-col gap-4 mb-8" name="filters-bar" onSubmit={handleSubmit}>
        <div className={clx('flex flex-col lg:flex-row items-center lg:items-start gap-2 my-2')}>
          <div className={clx('flex gap-2 items-center w-full')}>
            {searchBar && !!searchBar.choicesSource && (
              <SearchBar
                className="grow"
                label={searchBar.choicesSource[0].title}
                name="query"
                onReset={() => searchBar.choicesSource && onFieldReset(searchBar.choicesSource[0].source)}
                placeholder="..."
              />
            )}
            {showModal && (
              <Button
                className="h-fit"
                icon={clx('fa-solid', loading ? 'fa-circle-notch animate-spin' : 'fa-filters')}
                onClick={openModal}
                size={SIZE.MD}
              >
                <span className="hidden lg:inline-block">
                  <span className="mr-1">{t('filters')}</span>
                  <span
                    className={clx(activeFilterCount > 0 ? 'inline-block' : 'hidden')}
                  >{`(${activeFilterCount})`}</span>
                </span>
              </Button>
            )}
          </div>
        </div>
        <ActiveFilters
          filterData={filterData}
          filtersValues={filtersValues}
          onClear={onClearFilters}
          onFieldReset={onFieldReset}
        />
        <div className="flex items-start flex-wrap gap-4">
          {!!tabs?.length && (
            <div className="mr-auto max-w-full">
              <TabsLink items={tabs} shallowRouting={shallowRouting} />
            </div>
          )}
          {!!items?.length && (
            <div className="flex w-full justify-center">
              <BigTabs items={items} shallowRouting={shallowRouting} />
            </div>
          )}
        </div>
        <div className="flex flex-row justify-between items-center gap-4">
          <TotalResults maxResults={CARDS_MAX_RESULTS} totalItems={totalItems} />
          {withOrder && optionsOrder && (
            <DropDown
              items={(setOpen) =>
                optionsOrder.map(({ href, label, option }) => (
                  <Link
                    key={href}
                    href={href}
                    onClick={(e) => {
                      if (!updateQueryParams && setOrder) {
                        e.preventDefault();
                        setOrder(option.name, option.value);
                        setOpen(false);
                      }
                    }}
                  >
                    {label}
                  </Link>
                ))
              }
              trigger={
                <Button icon="fa-solid fa-sort" variant={BUTTON_VARIANTS.SECONDARY}>
                  {optionsOrder?.find(({ selected }) => selected)?.label || t('sort-by')}
                </Button>
              }
            />
          )}
        </div>
      </form>
    </FormProvider>
  );
};

export default ListHeader;
